import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { Shape } from '../../types/Shape';
import HTMLShapeBase from './HTMLShapeBase';
import 'highlight.js/styles/github.css';
import '../../styles/markdown.css';

interface HTMLMarkdownProps {
  shape: Shape;
  isSelected: boolean;
  showResizeHandles?: boolean;
  stagePos: { x: number; y: number };
  stageScale: number;
  isDrawing?: boolean;
  isEditing?: boolean;
  currentTool?: string;
  onDragStart?: (shapeId: string) => void;
  onDragMove?: (shapeId: string, newX: number, newY: number) => void;
  onDragEnd?: (shapeId: string) => void;
  onResize?: (shapeId: string, newWidth: number, newHeight: number, newX?: number, newY?: number) => void;
  onResizeEnd?: () => void;
  onClick?: (shapeId: string, e: React.MouseEvent) => void;
  onDoubleClick?: (shapeId: string, e: React.MouseEvent) => void;
  onContextMenu?: (shapeId: string, e: React.MouseEvent) => void;
  onMouseEnter?: (shapeId: string) => void;
  onMouseLeave?: () => void;
  onStageClick?: (canvasX: number, canvasY: number, e: React.MouseEvent) => void;
}

const HTMLMarkdown: React.FC<HTMLMarkdownProps> = (props) => {
  const { shape, isEditing = false, stageScale } = props;

  return (
    <HTMLShapeBase {...props}>
      <div
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: shape.fill || '#FFFFFF',
          border: `${(shape.strokeWidth || 1) * stageScale}px solid ${shape.stroke || '#E5E7EB'}`,
          borderRadius: `${8 * stageScale}px`,
          boxSizing: 'border-box',
          overflow: 'auto',
          padding: `${16 * stageScale}px`,
          pointerEvents: 'none',
        }}
      >
        {!isEditing && (
          <div
            style={{
              fontSize: `${(shape.fontSize || 14) * stageScale}px`,
              lineHeight: '1.6',
              color: '#1a1a1a',
              pointerEvents: 'auto',
              userSelect: 'text',
              cursor: 'text',
            }}
            className="markdown-content"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHighlight]}
              components={{
                // 헤딩
                h1: ({ node, ...props }) => <h1 style={{ fontSize: '2em', fontWeight: 'bold', marginTop: '0.67em', marginBottom: '0.67em' }} {...props} />,
                h2: ({ node, ...props }) => <h2 style={{ fontSize: '1.5em', fontWeight: 'bold', marginTop: '0.83em', marginBottom: '0.83em' }} {...props} />,
                h3: ({ node, ...props }) => <h3 style={{ fontSize: '1.17em', fontWeight: 'bold', marginTop: '1em', marginBottom: '1em' }} {...props} />,
                h4: ({ node, ...props }) => <h4 style={{ fontSize: '1em', fontWeight: 'bold', marginTop: '1.33em', marginBottom: '1.33em' }} {...props} />,
                h5: ({ node, ...props }) => <h5 style={{ fontSize: '0.83em', fontWeight: 'bold', marginTop: '1.67em', marginBottom: '1.67em' }} {...props} />,
                h6: ({ node, ...props }) => <h6 style={{ fontSize: '0.67em', fontWeight: 'bold', marginTop: '2.33em', marginBottom: '2.33em' }} {...props} />,

                // 리스트
                ul: ({ node, ...props }) => <ul style={{ marginTop: '0.5em', marginBottom: '0.5em', paddingLeft: '2em', listStyleType: 'disc' }} {...props} />,
                ol: ({ node, ...props }) => <ol style={{ marginTop: '0.5em', marginBottom: '0.5em', paddingLeft: '2em', listStyleType: 'decimal' }} {...props} />,
                li: ({ node, ...props }) => <li style={{ marginTop: '0.25em', marginBottom: '0.25em' }} {...props} />,

                // 코드
                code: (props: any) => {
                  const { node, inline, ...rest } = props;
                  return inline
                    ? <code style={{ backgroundColor: '#f6f8fa', padding: '0.2em 0.4em', borderRadius: '3px', fontSize: '0.9em', fontFamily: 'monospace' }} {...rest} />
                    : <code style={{ display: 'block', backgroundColor: '#f6f8fa', padding: '1em', borderRadius: '6px', overflow: 'auto', fontFamily: 'monospace', fontSize: '0.9em' }} {...rest} />;
                },

                pre: ({ node, ...props }) => <pre style={{ backgroundColor: '#f6f8fa', padding: '1em', borderRadius: '6px', overflow: 'auto', marginTop: '0.5em', marginBottom: '0.5em' }} {...props} />,

                // 인용구
                blockquote: ({ node, ...props }) => <blockquote style={{ borderLeft: '4px solid #e5e7eb', paddingLeft: '1em', marginLeft: 0, marginTop: '0.5em', marginBottom: '0.5em', color: '#6b7280' }} {...props} />,

                // 테이블
                table: ({ node, ...props }) => <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: '0.5em', marginBottom: '0.5em' }} {...props} />,
                th: ({ node, ...props }) => <th style={{ border: '1px solid #e5e7eb', padding: '0.5em', backgroundColor: '#f9fafb', fontWeight: 'bold', textAlign: 'left' }} {...props} />,
                td: ({ node, ...props }) => <td style={{ border: '1px solid #e5e7eb', padding: '0.5em' }} {...props} />,

                // 수평선
                hr: ({ node, ...props }) => <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', marginTop: '1.5em', marginBottom: '1.5em' }} {...props} />,

                // 링크
                a: ({ node, ...props }) => <a style={{ color: '#3b82f6', textDecoration: 'underline', pointerEvents: 'auto', cursor: 'pointer' }} {...props} />,

                // 강조
                strong: ({ node, ...props }) => <strong style={{ fontWeight: 'bold' }} {...props} />,
                em: ({ node, ...props }) => <em style={{ fontStyle: 'italic' }} {...props} />,

                // 삭제선
                del: ({ node, ...props }) => <del style={{ textDecoration: 'line-through' }} {...props} />,

                // 체크박스
                input: ({ node, ...props }) => <input style={{ marginRight: '0.5em' }} {...props} />,

                // 이미지
                img: ({ node, ...props }) => <img style={{ maxWidth: '100%', height: 'auto', borderRadius: '4px' }} {...props} />,

                // 문단
                p: ({ node, ...props }) => <p style={{ marginTop: '0.5em', marginBottom: '0.5em' }} {...props} />,
              }}
            >
              {shape.markdown || '# Markdown\n\n내용을 입력하세요...'}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </HTMLShapeBase>
  );
};

export default memo(HTMLMarkdown);

